import { useEffect, useState, useSyncExternalStore } from "react";
import type { KitchenTimer } from "../../../shared/kitchen/models";
import {
  ALARM_MAX_MS,
  formatCountdown,
  formatDuration,
  microwaveText,
  microwaveToSec,
  TIMER_PRESETS_MIN,
  timerPhase,
  timerRemainingMs,
  timerShouldRing,
} from "../../../shared/kitchen/timers";
import { useAppUi } from "../../app/uiContext";
import { readPref, writePref } from "../../data/persist";
import { dataStateOf } from "../../data/queries";
import { Icon } from "../../ui/Icon";
import { Sheet } from "../../ui/Sheet";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import { audioBlocked, onAudioStateChange, setAlarm } from "./alarm";
import { isTempId, serverNowMs, tempId, useTimerActions, useTimers } from "./api";
import { KGlyph } from "./glyphs";
import "./kitchen.css";

interface TimersConfig {
  sound: boolean;
}

const QUICK_LABELS = ["Pasta", "Rice", "Eggs", "Oven", "Tea", "Pizza"];
const MUTE_PREF = "kitchen.timers.muted";

/** 1 s tick, only while something is counting down. */
function useTicker(active: boolean) {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setN((n) => n + 1), 1000);
    const onVisible = () => document.visibilityState === "visible" && setN((n) => n + 1);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [active]);
}

function Timers({ instanceId, config, size, editing, fullscreen, openFullscreen }: WidgetProps<TimersConfig>) {
  const q = useTimers();
  const actions = useTimerActions();
  const { toast } = useAppUi();
  const state = dataStateOf(q, 2 * 60_000);
  const [mutedHere, setMutedHere] = useState(() => readPref(MUTE_PREF, false));
  const [adding, setAdding] = useState(false);
  const blocked = useSyncExternalStore(onAudioStateChange, audioBlocked, () => true);

  const skew = q.data?.skewMs ?? 0;
  const now = serverNowMs(skew);
  // Finished timers first (they need attention), then in the order they were started.
  const doneFirst = (t: KitchenTimer) => (timerPhase(t, now) === "done" ? 0 : 1);
  const timers = [...(q.data?.timers ?? [])].sort((a, b) => doneFirst(a) - doneFirst(b) || a.createdAt.localeCompare(b.createdAt));
  const anyRunning = timers.some((t) => timerPhase(t, now) === "running");
  const ringing = timers.filter((t) => timerShouldRing(t, now));
  // Tick only while something counts down or rings; a silenced "Done!" card needs no clock.
  useTicker(anyRunning || ringing.length > 0);

  const soundOn = config.sound && !mutedHere;
  // Ring while any finished timer is within its 2-minute window.
  const ringUntil = ringing.length ? Math.max(...ringing.map((t) => Date.parse(t.endAt!) + ALARM_MAX_MS)) - skew : null;
  useEffect(() => {
    const reason = `timers:${instanceId}`;
    setAlarm(reason, soundOn && ringUntil !== null && !editing ? ringUntil : null);
    return () => setAlarm(reason, null);
  }, [instanceId, soundOn, ringUntil, editing]);

  const fail = (what: string) => (e: Error) => toast(`Couldn’t ${what}: ${e.message}`);
  const start = (label: string, durationSec: number) => actions.start.mutate({ label, durationSec, tempId: tempId("tmr") }, { onError: fail("start the timer") });

  const toggleMute = () => {
    setMutedHere((m) => {
      writePref(MUTE_PREF, !m);
      return !m;
    });
  };

  const compact = size.variant === "compact" && !fullscreen;
  const running = timers.filter((t) => timerPhase(t, now) !== "done").length;

  return (
    <div className={`kt ${compact ? "is-compact" : ""}`}>
      <WidgetHeader
        title="Timers"
        meta={running ? `${running} running` : undefined}
        onTitle={fullscreen ? undefined : openFullscreen}
        actions={
          <>
            {config.sound && (
              <button className="icon-btn" onClick={toggleMute} aria-pressed={mutedHere} aria-label={mutedHere ? "Turn timer sound on for this screen" : "Mute timer sound on this screen"} disabled={editing}>
                <KGlyph name={mutedHere ? "mute" : "speaker"} />
              </button>
            )}
            <button className="icon-btn icon-btn-accent" onClick={() => setAdding(true)} aria-label="New timer" disabled={editing}>
              <Icon name="plus" />
            </button>
          </>
        }
      />
      {soundOn && blocked && ringing.length > 0 && <p className="kt-hint">Tap anywhere to allow the alarm sound on this screen.</p>}

      {!q.data ? (
        state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="clock" title="Timers unavailable" detail={q.error?.message} />
      ) : timers.length === 0 ? (
        <div className="kt-presets" role="group" aria-label="Start a timer">
          {TIMER_PRESETS_MIN.map((m) => (
            <button key={m} className="kt-preset" onClick={() => start(`${m} min`, m * 60)} disabled={editing}>
              <strong>{m}</strong>
              <span>min</span>
            </button>
          ))}
          <button className="kt-preset kt-preset-custom" onClick={() => setAdding(true)} disabled={editing}>
            <KGlyph name="timer" size="1.5rem" />
            <span>Custom</span>
          </button>
        </div>
      ) : (
        <ul className="kt-list">
          {timers.map((t) => (
            <TimerCard key={t.id} t={t} now={now} editing={editing} actions={actions} onError={fail} />
          ))}
        </ul>
      )}
      <StatusBadge state={state} updatedAt={q.dataUpdatedAt} />
      {adding && (
        <NewTimerSheet
          onClose={() => setAdding(false)}
          onStart={(label, sec) => {
            start(label, sec);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

function TimerCard({
  t,
  now,
  editing,
  actions,
  onError,
}: {
  t: KitchenTimer;
  now: number;
  editing: boolean;
  actions: ReturnType<typeof useTimerActions>;
  onError: (what: string) => (e: Error) => void;
}) {
  const phase = timerPhase(t, now);
  const left = timerRemainingMs(t, now);
  const pending = isTempId(t.id);
  const progress = phase === "done" ? 1 : Math.min(1, Math.max(0, 1 - left / Math.max(1, t.durationMs)));
  const disabled = editing || pending;

  if (phase === "done") {
    return (
      <li className="kt-card is-done" role="alert">
        <div className="kt-done-head">
          <KGlyph name="bell" size="1.6rem" className="kt-bell" />
          <span className="kt-label">{t.label}</span>
        </div>
        <span className="kt-digits">Done!</span>
        <div className="kt-actions">
          <button className="btn kt-snooze" onClick={() => actions.extend.mutate(t, { onError: onError("add a minute") })} disabled={disabled}>
            +1 min
          </button>
          <button className="btn btn-primary btn-lg kt-dismiss" onClick={() => actions.remove.mutate(t, { onError: onError("dismiss the timer") })} disabled={disabled}>
            Dismiss
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className={`kt-card is-${phase}`} style={{ ["--progress" as string]: progress }}>
      <div className="kt-top">
        <span className="kt-label">{t.label}</span>
        <span className="kt-total">{phase === "paused" ? "Paused" : formatDuration(t.durationMs)}</span>
        <button className="icon-btn kt-cancel" onClick={() => actions.remove.mutate(t, { onError: onError("cancel the timer") })} aria-label={`Cancel ${t.label} timer`} disabled={disabled}>
          <Icon name="close" size="1.1rem" />
        </button>
      </div>
      <span className="kt-digits" aria-live="off">
        {formatCountdown(left)}
      </span>
      <div className="kt-bar" aria-hidden="true">
        <i />
      </div>
      <div className="kt-actions">
        {phase === "paused" ? (
          <button className="btn kt-btn" onClick={() => actions.resume.mutate(t, { onError: onError("resume the timer") })} disabled={disabled}>
            <Icon name="play" size="1.1rem" /> Resume
          </button>
        ) : (
          <button className="btn kt-btn" onClick={() => actions.pause.mutate(t, { onError: onError("pause the timer") })} disabled={disabled}>
            <Icon name="pause" size="1.1rem" /> Pause
          </button>
        )}
        <button className="btn kt-btn" onClick={() => actions.extend.mutate(t, { onError: onError("add a minute") })} disabled={disabled}>
          +1 min
        </button>
      </div>
    </li>
  );
}

const PAD = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "00", "0", "back"] as const;

function NewTimerSheet({ onClose, onStart }: { onClose: () => void; onStart: (label: string, sec: number) => void }) {
  const [label, setLabel] = useState("");
  const [digits, setDigits] = useState("");
  const sec = microwaveToSec(digits);
  const press = (k: (typeof PAD)[number]) => setDigits((d) => (k === "back" ? d.slice(0, -1) : (d + k).replace(/^0+/, "").slice(0, 6)));
  const name = (fallback: string) => label.trim() || fallback;

  return (
    <Sheet
      title="New timer"
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={() => onStart(name(formatDuration(sec * 1000)), sec)} disabled={sec < 1 || sec > 86_400}>
            Start {sec > 0 ? microwaveText(digits) : ""}
          </button>
        </>
      }
    >
      <div className="form">
        <label className="field">
          <span>Name (optional)</span>
          <input className="input input-lg" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Pasta" maxLength={40} />
        </label>
        <div className="chip-row wrap">
          {QUICK_LABELS.map((l) => (
            <button key={l} className={`chip chip-sm ${label === l ? "on" : ""}`} onClick={() => setLabel(label === l ? "" : l)}>
              {l}
            </button>
          ))}
        </div>
        <div className="field">
          <span>Quick start</span>
          <div className="kt-presets kt-presets-sheet">
            {TIMER_PRESETS_MIN.map((m) => (
              <button key={m} className="kt-preset" onClick={() => onStart(name(`${m} min`), m * 60)}>
                <strong>{m}</strong>
                <span>min</span>
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <span>Or set a time</span>
          <div className="kt-entry">
            <output className={`kt-entry-digits ${digits ? "" : "is-empty"}`} aria-live="polite">
              {microwaveText(digits)}
            </output>
            <div className="kt-pad">
              {PAD.map((k) => (
                <button key={k} className="kk-key" onClick={() => press(k)} aria-label={k === "back" ? "Delete digit" : k} disabled={k !== "back" && digits.length >= 6}>
                  {k === "back" ? <KGlyph name="backspace" /> : k}
                </button>
              ))}
            </div>
            <p className="muted small">Type minutes and seconds, like a microwave: 1 3 0 is 1:30.</p>
          </div>
        </div>
      </div>
    </Sheet>
  );
}

export const timersWidget: WidgetDefinition<TimersConfig> = {
  type: "timers",
  version: 1,
  name: "Kitchen timers",
  description: "Several named timers at once, shared by every screen. Rings when done.",
  icon: "clock",
  category: "household",
  defaultSize: { w: 4, h: 4 },
  minSize: { w: 3, h: 2 },
  stackHeight: 360,
  defaults: { sound: true },
  settings: [{ key: "sound", label: "Play alarm sound", type: "toggle", help: "Each screen can also mute itself from the speaker button on the card." }],
  provider: "kinmantle-kitchen",
  supportsFullscreen: true,
  Component: Timers,
};
